import { createAsyncThunk } from "@reduxjs/toolkit";
import { ADMIN } from "constants/api";
import axios from "utils/http";

interface IFetchAdminUsers extends ApiResponse {
  data: IAdminUserData;
}

interface IFetchAdminUsersArgs extends ReduxAction {
  page: number;
}

export interface IInviteAdminResponse extends ReduxAction {
  data: string;
}

export interface IInviteAdminArgs extends ReduxAction {
  payload: IInviteAdmin & { otp_code: string };
}

export interface IEditeAdminUserArgs extends ReduxAction {
  payload: any;
}

export interface IDeleteAdminUserArgs extends ReduxAction {
  payload: any;
}

export interface IFetchAdminUserWithPermissionsArgs extends ReduxAction {
  payload: { id: number };
}

export interface IFetchPermissions extends ApiResponse {
  data: IPermissionItem[]
}

export interface IFetchRoles extends ApiResponse {
  data: IRoleItem[]
}

interface IFetchAdminUserWithPermissions extends ApiResponse {
  data: IAdminUserWithPermissions;
}

export interface IEditAdminUserPermissionsArgs extends ReduxAction {
  payload: { id: number; permissions: number[] };
}

interface IEditAdminUserPermissions extends ApiResponse {
  data: string;
}

export const fetchAdminUsers = createAsyncThunk(
  ADMIN.LIST,
  async ({ page, onFailure }: IFetchAdminUsersArgs, { rejectWithValue }) => {
    try {
      const { data }: IFetchAdminUsers = await axios.get(ADMIN.LIST, {
        params: { page },
      });

      return data;
    } catch (error) {
      if (onFailure) onFailure(error as IErrorResponse);
      return rejectWithValue({ error });
    }
  }
);

export const fetchPermissions = createAsyncThunk(
  ADMIN.PERMISSIONS,
  async ({ onFailure }: ReduxAction, { rejectWithValue }) => {
    try {
      const { data }: IFetchPermissions = await axios.get(ADMIN.PERMISSIONS);

      return data;
    } catch (error) {
      if (onFailure) onFailure(error as IErrorResponse);
      return rejectWithValue({ error });
    }
  }
);

export const fetchAdminUserWithPermissions = createAsyncThunk(
  ADMIN.USER_PERMISSIONS,
  async (
    { payload, onSuccess, onFailure }: IFetchAdminUserWithPermissionsArgs,
    { rejectWithValue }
  ) => {
    try {
      const { data }: IFetchAdminUserWithPermissions = await axios.get(
        ADMIN.USER_PERMISSIONS.replace(":id", String(payload.id))
      );
      if (onSuccess) onSuccess(data);

      return data;
    } catch (error) {
      if (onFailure) onFailure(error as IErrorResponse);
      return rejectWithValue({ error });
    }
  }
);

export const fetchRoles = createAsyncThunk(
  ADMIN.ROLES,
  async ({ onFailure }: ReduxAction, { rejectWithValue }) => {
    try {
      const { data }: IFetchRoles = await axios.get(ADMIN.ROLES);

      return data;
    } catch (error) {
      if (onFailure) onFailure(error as IErrorResponse);
      return rejectWithValue({ error });
    }
  }
);

export const inviteAdmin = createAsyncThunk(
  ADMIN.INVITE,
  async (
    { payload, onSuccess, onFailure }: IInviteAdminArgs,
    { rejectWithValue }
  ) => {
    try {
      const { data }: IInviteAdminResponse = await axios.post(
        ADMIN.INVITE,
        payload
      );
      if (onSuccess) onSuccess(data);
      return data;
    } catch (error) {
      if (onFailure) onFailure(error as IErrorResponse);
      return rejectWithValue({ error });
    }
  }
);

export const resendAdminInvitation = createAsyncThunk<
  ApiResponse,
  { adminId: number; otp_code: string },
  { rejectValue: IErrorResponse }
>(ADMIN.RESEND_INVITE, async ({ adminId, otp_code }, { rejectWithValue }) => {
  try {
    const response: ApiResponse = await axios.post(
      ADMIN.RESEND_INVITE.replace(":admin", String(adminId)),
      { otp_code }
    );
    return response;
  } catch (error) {
    return rejectWithValue(error as IErrorResponse);
  }
});

export const editAdminUser = createAsyncThunk(
  ADMIN.EDIT,
  async (
    { payload, onSuccess, onFailure }: IEditeAdminUserArgs,
    { rejectWithValue }
  ) => {
    try {
      const { data }: any = await axios.put(
        ADMIN.EDIT.replace(":id", String(payload?.id)),
        payload
      );
      if (onSuccess) onSuccess(data);
      return data;
    } catch (error) {
      if (onFailure) onFailure(error as IErrorResponse);
      return rejectWithValue({ error });
    }
  }
);

export const editAdminUserPermissions = createAsyncThunk(
  ADMIN.UPDATE_USER_PERMISSIONS,
  async (
    { payload, onSuccess, onFailure }: IEditAdminUserPermissionsArgs,
    { rejectWithValue }
  ) => {
    try {
      const { data, message }: IEditAdminUserPermissions = await axios.patch(
        ADMIN.UPDATE_USER_PERMISSIONS.replace(":id", String(payload.id)),
        { permissions: payload.permissions }
      );
      if (onSuccess) onSuccess(message);
      return data;
    } catch (error) {
      if (onFailure) onFailure(error as IErrorResponse);
      return rejectWithValue({ error });
    }
  }
);

export const deleteAdminUser = createAsyncThunk(
  ADMIN.DELETE,
  async (
    { payload, onSuccess, onFailure }: IDeleteAdminUserArgs,
    { rejectWithValue }
  ) => {
    try {
      const apiEndpoint = ADMIN.DELETE.replace(":id", String(payload?.id));
      const { data }: any = await axios.delete(apiEndpoint);

      if (onSuccess) onSuccess(data);
      return data;
    } catch (error) {
      if (onFailure) onFailure(error as IErrorResponse);
      return rejectWithValue({ error });
    }
  }
);
